import {createSlice, PayloadAction} from "@reduxjs/toolkit";

import {listenerMiddleware} from "../../redux/listenerMiddleware";
import {authenticationSlice} from "../authentication/authenticationSlice";
import {convertToDateTime} from "../common/dateUtils/convertToDateTime";
import {DateTime} from "luxon";

export interface OrdinamentoLista {
    asc: boolean;
    campo: string;
}

export interface PreferenzeLista {
    numero_record?: number;
    page?: number;
    fase_collezione?: string[];
    search?: string
    ordinamento?: OrdinamentoLista;
}

export interface PreferenzeUtente {
    drawer_aperto: boolean;
    dark_mode: boolean;
}

export interface UserStored {
    username?: string;
    // password?: string;
    rememberUsername?: boolean;
    // dataScadenzaPassword?: string;
    email?: string;
    access?: string;
    refresh?: string;
    refreshExpiresAt?: string;
    timeRefresh?: string;
}

export interface FiltriPrevisioni extends PreferenzeLista {
    mandatario?: number;      // Filtro singolo
    brand?: number;           // Filtro singolo
    confezionista?: number;   // Filtro singolo
    stagione?: number;        // Filtro singolo
    commerciale?: number;     // Filtro singolo
    tecnica?: number[];       // Filtro multiplo (array)
    tutti?: boolean;          // Per fetch senza paginazione (PDF)
}

export interface FiltriBudgets extends PreferenzeLista {
    mandatario?: number;      // Filtro singolo
    brand?: number;           // Filtro singolo
    confezionista?: number;   // Filtro singolo
    stagione?: number;        // Filtro singolo
    commerciale?: number;     // Filtro singolo
    tecnicheSelezionate?: number[];       // Filtro multiplo (array)
}

export interface FiltriOrdiniLavoro extends PreferenzeLista {
    // Filtro stato (multiplo)
    stato?: string[];

    // Filtri date arrivo prevista (range)
    data_da?: string;
    data_a?: string;

    // Filtri tramite previsione (singoli)
    stagione_id?: number;
    mandatario_id?: number;
    confezionista_id?: number;
    commerciale_id?: number;

    // Filtro tecnica (multiplo)
    tecnica_id?: number[];

    // Filtro previsione diretta
    previsione_id?: number;
    mostraTuttiOrdiniGantt?: boolean;

    // Ordinamento (es. '-id', 'data_arrivo_prevista', '-data_consegna_prevista')
    ordering?: string;
}

export interface FiltriGantt {
    data_da?: string;
    numeroSettimane?:  1 | 2 | 3 | 4 | 6;
    tecnica_id?: number[];
}

export interface PreferenzeUtenteState {
    userStored?: UserStored;
    preferenzeUtente: PreferenzeUtente;
    preferenzeListaTasks: PreferenzeLista;
    filtriPrevisioni?: FiltriPrevisioni;
    filtriBudgets?: FiltriBudgets;
    filtriOrdiniLavoro?: FiltriOrdiniLavoro;
    filtriGantt?: FiltriGantt;
}

const initialState: PreferenzeUtenteState = {
    userStored: undefined,
    preferenzeUtente: {drawer_aperto: true, dark_mode: false},
    preferenzeListaTasks: {numero_record: 20, page: 1},
    filtriPrevisioni: { numero_record: 20, page: 1, fase_collezione: ['PROT', 'CAMP', 'AN'] },
    filtriBudgets: { numero_record: 20, page: 1 },
    filtriOrdiniLavoro: {
        numero_record: 20,
        page: 1,
        stato: ['CONF', 'ARR'],  // Stati default attivi
        ordering: 'data_consegna_prevista',  // Ordinamento default crescente
        mostraTuttiOrdiniGantt: false,

    },
    filtriGantt:{
        data_da: DateTime.now().startOf("week").toFormat('yyyy-mm-dd'),
        numeroSettimane: 2,
        tecnica_id: [],
    },
};

export const userPreferenceSlice = createSlice({
    name: "userPreference",
    initialState,
    reducers: {
        setDrawerAperto: (state, action: PayloadAction<boolean>) => {
            state.preferenzeUtente.drawer_aperto = action.payload;
        },
        setUserStored: (state, action: PayloadAction<UserStored>) => {
            state.userStored = action.payload;
        },

        tokenRefresh: (
            state,
            action: PayloadAction<{
                access?: string;
                refresh?: string;
            }>
        ) => {
            if (state.userStored) {
                state.userStored = {
                    ...state.userStored,
                    ...action.payload,
                    refreshExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                };
            }
        },

        resetUserStored: (state) => {
            state.userStored = undefined;
        },

        updateRememberedUsername: (state, action: PayloadAction<{username: string, remember: boolean}>) => {
            if (state.userStored) {
                state.userStored.username = action.payload.username;
                state.userStored.rememberUsername = action.payload.remember;
            } else if (action.payload.remember) {
                state.userStored = {
                    username: action.payload.username,
                    rememberUsername: true
                };
            }
        },

        setPreferenzeListaTasks: (
            state,
            action: PayloadAction<PreferenzeLista>
        ) => {
            state.preferenzeListaTasks = action.payload;
        },

        setFiltriPrevisioni: (state, action: PayloadAction<FiltriPrevisioni>) => {
            state.filtriPrevisioni = { ...state.filtriPrevisioni, ...action.payload, search: '' };
        },
        resetFiltriPrevisioni: (state) => {
            state.filtriPrevisioni = { numero_record: 10, page: 1 };
        },

        setFiltriBudgets: (state, action: PayloadAction<FiltriBudgets>) => {
            state.filtriBudgets = { ...state.filtriBudgets, ...action.payload, search: '' };
        },
        resetFiltriBudgets: (state) => {
            state.filtriBudgets = { numero_record: 10, page: 1 };
        },

        setFiltriOrdiniLavoro: (state, action: PayloadAction<FiltriOrdiniLavoro>) => {
            state.filtriOrdiniLavoro = { ...state.filtriOrdiniLavoro, ...action.payload };
        },
        resetFiltriOrdiniLavoro: (state) => {
            state.filtriOrdiniLavoro = {
                numero_record: 20,
                page: 1,
                stato: ['CONF', 'ARR'],
                ordering: '-id'
            };
        },
        setFiltriGantt:(state, action: PayloadAction<FiltriGantt>) => {
            state.filtriGantt = { ...state.filtriGantt, ...action.payload };
        },
        resetFiltriGantt:(state) => {
            state.filtriGantt = {
                data_da: DateTime.now().startOf("week").toFormat('yyyy-mm-dd'),
                numeroSettimane: 2,
                tecnica_id: []
            };
        }
    },
    extraReducers: (builder) => {

    },
});

// Action creators are generated for each case reducer function
export const {
    setDrawerAperto,
    setUserStored,
    tokenRefresh,
    resetUserStored,
    updateRememberedUsername,
    setPreferenzeListaTasks,
    setFiltriPrevisioni,
    resetFiltriPrevisioni,
    setFiltriBudgets,
    resetFiltriBudgets,
    setFiltriOrdiniLavoro,
    resetFiltriOrdiniLavoro,
    setFiltriGantt,
    resetFiltriGantt
} = userPreferenceSlice.actions;

listenerMiddleware.startListening({
    actionCreator: authenticationSlice.actions.resetUtente,
    effect: (action, listenerApi) => {
        listenerApi.dispatch(userPreferenceSlice.actions.resetUserStored());
    },
});

export const userPreferenceReducer = userPreferenceSlice.reducer;
